import CheckIcon from "@mui/icons-material/Check";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";

import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnalyticsFilterDashboard } from "@/features/deployment-dashboard/widgets/panels/analytics-filter-dashboard";
import { PopulationTrendAnalyticsChart } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import { QueueWaitTrendChart } from "@/features/deployment-dashboard/widgets/charts/queue-wait-trend";
import { useAreaAois } from "@/features/deployment-dashboard/live-view/hooks/use-area-aois";
import { deriveAoiMapLayers } from "@/features/deployment-dashboard/live-view/utils/derive-aoi-map-layers";
import { deriveObservationAreaBoundaries } from "@/features/deployment-dashboard/live-view/utils/derive-observation-area-boundaries";
import { mapTrackPositionsToSchematicSpace } from "@/features/deployment-dashboard/live-view/utils/map-track-positions-to-schematic";
import { LIVE_HEATMAP_CONFIG } from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import { useSelectedDeployment } from "@/features/deployment-selection/hooks/use-selected-deployment";
import { FacilitySchematic } from "@/features/deployment-dashboard/widgets/panels/facility-schematic";
import { useFacilitySchematicAssets } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/hooks/use-facility-schematic-assets";
import { LayersSelect } from "@/features/deployment-dashboard/shared/layers-select";
import { env } from "@/shared/config/env";
import { Callout, ConsoleSurface } from "@/shared/ui";
import type {
  AnalyticsFilters,
  AreaLocation,
  LiveTrackPoint,
  LiveTrailSegment,
} from "@/features/deployment-dashboard/types";
import { usePopulationTrendAnalytics } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import { useDensityTrend } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import { useQueueMetrics } from "@/features/deployment-dashboard/analytics/hooks/use-queue-metrics";
import {
  usePlaybackBuffer,
  type PlaybackFrameData,
} from "@/features/deployment-dashboard/playback/use-playback-buffer";
import { getPlaybackClockTimestamp } from "@/features/deployment-dashboard/playback/playback-clock";
import {
  getPlaybackQualityForDuration,
  getPlaybackQualityOption,
  isPlaybackQuality,
  PLAYBACK_DATE_RANGE_CONFIG,
} from "@/features/deployment-dashboard/playback/playback-quality";
import { formatCount } from "@/features/deployment-dashboard/shared/helpers";
import { useAreas } from "@/features/deployment-dashboard/shared/hooks/use-areas";
import { MetricCard } from "@/features/deployment-dashboard/widgets/metrics/dashboard-metrics";
import type { DashboardWidgetIssue } from "@/features/deployment-dashboard/widgets/dashboard-widget-issue-indicator";
import {
  worldToFloorplanPixel,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import { PeopleIcon, TrendingUpIcon } from "@/shared/ui/console-icons";
import type { MultiLineChartPin } from "@/shared/charts";
import { browserStorage, storageEntries } from "@/shared/browser-storage";

const MAP_HEIGHT = 540;
const SCRUBBER_HEIGHT = 152;

const ONE_DAY_MS = 24 * 60 * 60 * 1_000;
const PLAYBACK_TICK_MS = 250;
const PLAYBACK_SPEEDS = [1, 2, 3, 4, 5] as const;
const PLAYBACK_AUTO_PAUSE_MINUTES = env.VITE_HISTORICAL_PLAYBACK_AUTO_PAUSE_MINUTES;
const PLAYBACK_AUTO_PAUSE_MS = PLAYBACK_AUTO_PAUSE_MINUTES * 60_000;

const TIME_FORMATTER = new Intl.DateTimeFormat(undefined, { timeStyle: "medium" });
const DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "medium",
});

type PlaybackArea = AreaLocation;

function getTodayPlaybackRange(): Pick<AnalyticsFilters, "startDate" | "endDate"> {
  const endDate = new Date();
  return {
    startDate: new Date(endDate.getTime() - PLAYBACK_DATE_RANGE_CONFIG.minimumDurationMs),
    endDate,
  };
}

function formatPlaybackTime(timestamp: number, includesMultipleDays: boolean): string {
  return (includesMultipleDays ? DATE_TIME_FORMATTER : TIME_FORMATTER).format(timestamp);
}

function getPlaybackArea(areaId: string, areas: AreaLocation[]): PlaybackArea | undefined {
  const area = areas.find((candidate) => candidate.id === areaId);
  if (!area) return undefined;

  return area;
}

function findNearestPlaybackPoint<T extends { timestamp: string }>(
  points: readonly T[],
  timestamp: number
): T | undefined {
  let nearestPoint: T | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const point of points) {
    const pointTimestamp = Date.parse(point.timestamp);
    if (!Number.isFinite(pointTimestamp)) continue;

    const distance = Math.abs(pointTimestamp - timestamp);
    if (distance < nearestDistance) {
      nearestPoint = point;
      nearestDistance = distance;
    }
  }

  return nearestPoint;
}

function getPlaybackMetricContentState(
  filtersApplied: boolean,
  loading: boolean,
  error: Error | null,
  hasValue: boolean
): "loading" | "empty" | "waiting" | undefined {
  if (!filtersApplied) return "waiting";
  if (loading) return "loading";
  if (error != null || !hasValue) return "empty";
  return undefined;
}

function getPlaybackMetricIssue(
  label: string,
  error: Error | null
): DashboardWidgetIssue | undefined {
  if (error == null) return undefined;

  return {
    severity: "error",
    title: `${label} unavailable`,
    description: error.message || `Unable to load ${label.toLowerCase()} data.`,
  };
}

function createPlaybackTracks(
  frame: PlaybackFrameData | undefined,
  transform: Pick<WorldToPixelTransformAsset, "matrix" | "sourceFrame"> | undefined
): LiveTrackPoint[] {
  if (!frame || !transform || frame.reference_frame_id !== transform.sourceFrame) return [];

  return mapTrackPositionsToSchematicSpace(
    transform,
    frame.objects.map((object) => ({
      id: `${frame.area_id}:${object.object_id}`,
      areaId: frame.area_id,
      objectId: object.object_id,
      x: object.position.x,
      y: object.position.y,
    })),
    LIVE_HEATMAP_CONFIG.pointRadiusInMeters
  );
}

function createPlaybackTrails(
  recentFrames: PlaybackFrameData[],
  transform: Pick<WorldToPixelTransformAsset, "matrix" | "sourceFrame"> | undefined
): LiveTrailSegment[] {
  if (!transform) return [];
  const trails: LiveTrailSegment[] = [];
  for (let index = 1; index < recentFrames.length; index += 1) {
    const previous = recentFrames.at(index - 1);
    const current = recentFrames.at(index);
    if (
      !previous ||
      !current ||
      previous.reference_frame_id !== transform.sourceFrame ||
      current.reference_frame_id !== transform.sourceFrame ||
      previous.reference_frame_id !== current.reference_frame_id
    ) {
      continue;
    }
    const previousObjectsByTrack = new Map(
      previous.objects.flatMap((object) =>
        object.track_id == null ? [] : [[object.track_id, object] as const]
      )
    );
    for (const object of current.objects) {
      if (!object.track_id) continue;
      const earlier = previousObjectsByTrack.get(object.track_id);
      if (!earlier) continue;
      const start = worldToFloorplanPixel(transform, earlier.position.x, earlier.position.y);
      const end = worldToFloorplanPixel(transform, object.position.x, object.position.y);
      trails.push({
        id: `${object.track_id}:${current.frame_time}`,
        start: [start.x, start.y],
        end: [end.x, end.y],
        opacity: index / recentFrames.length,
      });
    }
  }
  return trails;
}

export function PlaybackContainer() {
  const { deploymentId } = useSelectedDeployment();
  return <DeploymentPlaybackContainer key={deploymentId} deploymentId={deploymentId} />;
}

function DeploymentPlaybackContainer({ deploymentId }: { deploymentId: string }) {
  const theme = useTheme();
  const [filters, setFilters] = useState<AnalyticsFilters>(() => ({
    ...getTodayPlaybackRange(),
    areaId: "",
  }));
  const [draftFilters, setDraftFilters] = useState<AnalyticsFilters>(filters);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [playbackTimestamp, setPlaybackTimestamp] = useState(() => filters.startDate.getTime());
  const [timelineTimestamp, setTimelineTimestamp] = useState(playbackTimestamp);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<(typeof PLAYBACK_SPEEDS)[number]>(1);
  const [playbackSpeedMenuAnchor, setPlaybackSpeedMenuAnchor] = useState<HTMLElement | null>(null);
  const [wasAutoPaused, setWasAutoPaused] = useState(false);
  const [hoveredTimestamp, setHoveredTimestamp] = useState<number | null>(null);
  const [isSeekingPlaybackPosition, setIsSeekingPlaybackPosition] = useState(false);
  const [isBufferingPlaybackPosition, setIsBufferingPlaybackPosition] = useState(false);
  const [playbackSettings, setPlaybackSettings] = useState(
    () => browserStorage.read(storageEntries.playbackSettings) ?? { pauseOnSeek: false }
  );
  const [isPlaybackSettingsOpen, setIsPlaybackSettingsOpen] = useState(false);
  const [quality, setQuality] = useState(() =>
    getPlaybackQualityForDuration(filters.endDate.getTime() - filters.startDate.getTime())
  );
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showSensors, setShowSensors] = useState(true);
  const [showDetections, setShowDetections] = useState(true);
  const [showTracks, setShowTracks] = useState(false);
  const seekRequestIdRef = useRef(0);
  const playbackClockRef = useRef<{ wallTime: number; playbackTime: number } | null>(null);
  const qualityChangeTimestampRef = useRef<number | null>(null);
  const hoverAnimationFrameRef = useRef<number | null>(null);
  const isSeekingPlaybackPositionRef = useRef(false);
  const resumePlaybackAfterSeekRef = useRef(false);

  const { data: areas = [], isSuccess: areasLoaded } = useAreas(deploymentId);
  const selectedArea = useMemo(
    () => getPlaybackArea(filters.areaId, areas),
    [areas, filters.areaId]
  );
  const selectedAreaId = selectedArea?.id ?? filters.areaId;
  const selectedAreaIds = selectedArea == null ? [] : [selectedArea.id];
  const { aois: areaAois } = useAreaAois(deploymentId, selectedAreaIds);
  const {
    transform,
    floorplanImageSize,
    observationAreas: rawObservationAreas,
  } = useFacilitySchematicAssets(deploymentId, selectedArea?.id);
  const populationAnalytics = usePopulationTrendAnalytics({
    deploymentId,
    areaIds: selectedAreaIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
    enabled: filtersApplied,
  });
  const densityAnalytics = useDensityTrend({
    deploymentId,
    areaIds: selectedAreaIds,
    startDate: filters.startDate,
    endDate: filters.endDate,
    enabled: filtersApplied,
  });
  const queueMetrics = useQueueMetrics(
    deploymentId,
    selectedAreaIds,
    filters.startDate,
    filters.endDate,
    filtersApplied
  );
  const aois = useMemo(
    () => (selectedArea == null ? [] : areaAois.filter((aoi) => aoi.areaId === selectedArea.id)),
    [areaAois, selectedArea]
  );
  const observationAreas = useMemo(
    () => deriveObservationAreaBoundaries(transform, rawObservationAreas),
    [rawObservationAreas, transform]
  );
  const aoiMapLayers = useMemo(
    () => deriveAoiMapLayers(transform, aois, null, [], { editorMode: null }),
    [aois, transform]
  );
  const startTime = filters.startDate.toISOString();
  const endTime = filters.endDate.toISOString();
  const qualityOption = getPlaybackQualityOption(quality);
  const playbackBuffer = usePlaybackBuffer({
    deploymentId,
    areaId: selectedAreaId,
    startTime,
    endTime,
    quality,
  });

  useEffect(() => {
    if (!areasLoaded || areas.length === 0 || draftFilters.areaId.length > 0) return;
    const savedAreaId = browserStorage.read(storageEntries.preferredArea(deploymentId));
    const initialArea = areas.find((area) => area.id === savedAreaId) ?? areas[0];
    if (!initialArea) return;

    setDraftFilters((current) => ({ ...current, areaId: initialArea.id }));
  }, [areas, areasLoaded, deploymentId, draftFilters.areaId]);

  const {
    manifest,
    loadedSegmentIndexes,
    loadingSegmentIndexes,
    error: playbackError,
    ensureSegment,
    ensureSegmentWindow,
    retrySegment,
    retainSegmentWindow,
    getBucketedBufferedFrame,
    getBufferedFrameWindow,
  } = playbackBuffer;
  const playbackStep = manifest?.sample_interval_ms ?? qualityOption.sampleIntervalMs;
  const manifestStart = manifest == null ? null : Date.parse(manifest.effective_start_time);
  const manifestEnd = manifest == null ? null : Date.parse(manifest.effective_end_time);
  const requestedStart = filters.startDate.getTime();
  const requestedEnd = filters.endDate.getTime();
  const playbackStart = manifestStart ?? requestedStart;
  const playbackEnd = manifestEnd == null ? requestedEnd : Math.max(playbackStart, manifestEnd - 1);
  const hasPlaybackRange = manifest != null && playbackEnd >= playbackStart;
  const hasPlayablePlaybackData =
    hasPlaybackRange &&
    (manifest?.segments.some((segment) => segment.availability !== "empty") ?? false);
  const hasNoPlaybackData = manifest != null && !hasPlayablePlaybackData;
  const currentPlaybackSegment = manifest?.segments.find((segment) => {
    const start = Date.parse(segment.start_time);
    const end = Date.parse(segment.end_time);
    return playbackTimestamp >= start && playbackTimestamp < end;
  });
  const isPlaybackMapBuffering =
    filtersApplied &&
    playbackError === null &&
    (manifest === null ||
      isBufferingPlaybackPosition ||
      (currentPlaybackSegment !== undefined &&
        currentPlaybackSegment.availability !== "empty" &&
        !loadedSegmentIndexes.has(currentPlaybackSegment.index)));
  const playbackDuration = Math.max(1, playbackEnd - playbackStart);
  const frameCount = Math.max(1, Math.floor(playbackDuration / playbackStep));
  const includesMultipleDays = playbackDuration > ONE_DAY_MS;
  const majorTickCount = Math.min(24, Math.max(1, Math.ceil(playbackDuration / (60 * 60 * 1_000))));
  const majorTickSpacing = 100 / majorTickCount;
  const chartXAxisRange = useMemo(
    () => ({ min: requestedStart, max: requestedEnd }),
    [requestedEnd, requestedStart]
  );
  const populationTrend = populationAnalytics.data;
  const densityTrend = densityAnalytics.data;
  const playbackPopulationPoint = useMemo(
    () => findNearestPlaybackPoint(populationTrend, playbackTimestamp),
    [playbackTimestamp, populationTrend]
  );
  const playbackDensityPoint = useMemo(
    () => findNearestPlaybackPoint(densityTrend, playbackTimestamp),
    [densityTrend, playbackTimestamp]
  );
  const playbackDensity = playbackDensityPoint?.density ?? null;
  const populationMetricHasValue =
    playbackPopulationPoint?.averageTotalPopulation != null ||
    playbackPopulationPoint?.peakTotalPopulation != null;
  const populationMetricContentState = getPlaybackMetricContentState(
    filtersApplied,
    populationAnalytics.loading || isBufferingPlaybackPosition,
    populationAnalytics.error,
    populationMetricHasValue
  );
  const densityMetricContentState = getPlaybackMetricContentState(
    filtersApplied,
    densityAnalytics.loading || isBufferingPlaybackPosition,
    densityAnalytics.error,
    playbackDensity != null
  );
  const populationMetricIssue = getPlaybackMetricIssue("Population", populationAnalytics.error);
  const densityMetricIssue = getPlaybackMetricIssue("Density", densityAnalytics.error);
  const timelineAvailabilityBackground = useMemo(() => {
    if (!manifest || !hasPlaybackRange) return "none";
    const stops = manifest.segments.flatMap((segment) => {
      const start = ((Date.parse(segment.start_time) - playbackStart) / playbackDuration) * 100;
      const end = ((Date.parse(segment.end_time) - playbackStart) / playbackDuration) * 100;
      if (end <= 0 || start >= 100) return [];
      const color =
        segment.availability === "empty"
          ? "rgba(132, 147, 170, 0.18)"
          : loadedSegmentIndexes.has(segment.index)
            ? "rgba(33, 184, 166, 0.45)"
            : "rgba(132, 147, 170, 0.3)";
      return [`${color} ${Math.max(0, start)}%`, `${color} ${Math.min(100, end)}%`];
    });
    return stops.length === 0 ? "none" : `linear-gradient(90deg, ${stops.join(", ")})`;
  }, [hasPlaybackRange, loadedSegmentIndexes, manifest, playbackDuration, playbackStart]);
  const playbackFrame = useMemo(
    () =>
      loadedSegmentIndexes.size === 0 ? undefined : getBucketedBufferedFrame(playbackTimestamp),
    [getBucketedBufferedFrame, loadedSegmentIndexes, playbackTimestamp]
  );
  const recentFrames = useMemo(
    () => (loadedSegmentIndexes.size === 0 ? [] : getBufferedFrameWindow(playbackTimestamp, 4)),
    [getBufferedFrameWindow, loadedSegmentIndexes, playbackTimestamp]
  );
  const liveTracks = useMemo(
    () => createPlaybackTracks(playbackFrame, transform),
    [playbackFrame, transform]
  );
  const trails = useMemo(
    () => createPlaybackTrails(recentFrames, transform),
    [recentFrames, transform]
  );
  const referenceFrameMismatch =
    playbackFrame != null &&
    transform != null &&
    playbackFrame.reference_frame_id !== transform.sourceFrame;

  const timelineHoverPins = useMemo<readonly MultiLineChartPin[]>(() => {
    if (hoveredTimestamp == null) return [];

    return [
      {
        id: "timeline-hover",
        timestamp: hoveredTimestamp,
        color: theme.palette.warning.light,
        lineType: "dotted",
        tooltipBackgroundColor: alpha(theme.palette.warning.light, 0.9),
        tooltipTextColor: theme.palette.background.default,
      },
    ];
  }, [hoveredTimestamp, theme.palette.background.default, theme.palette.warning.light]);

  const commitPlaybackTimestamp = useCallback(
    async (timestamp: number): Promise<boolean> => {
      const requestId = seekRequestIdRef.current + 1;
      seekRequestIdRef.current = requestId;
      const segment = manifest?.segments.find((candidate) => {
        const start = Date.parse(candidate.start_time);
        const end = Date.parse(candidate.end_time);
        return timestamp >= start && timestamp < end;
      });
      if (!segment) return false;
      retainSegmentWindow(Math.max(0, segment.index - 1), segment.index + 2);
      setIsBufferingPlaybackPosition(true);
      try {
        const isBuffered = await ensureSegment(segment.index);
        if (!isBuffered || seekRequestIdRef.current !== requestId) return false;
        // The playback clock is authoritative. A missing bucket is a map gap,
        // not a reason to move time to a nearby observation.
        setPlaybackTimestamp(timestamp);
        setTimelineTimestamp(timestamp);
        return true;
      } finally {
        if (seekRequestIdRef.current === requestId) setIsBufferingPlaybackPosition(false);
      }
    },
    [ensureSegment, manifest, retainSegmentWindow]
  );

  useEffect(() => {
    if (!manifest) return;
    const manifestStartTime = Date.parse(manifest.effective_start_time);
    const manifestEndTime = Date.parse(manifest.effective_end_time);
    const latestPlayableTime = Math.max(
      manifestStartTime,
      manifestEndTime - manifest.sample_interval_ms
    );
    const preservedTimestamp = qualityChangeTimestampRef.current;
    qualityChangeTimestampRef.current = null;
    const timestamp =
      preservedTimestamp == null
        ? manifestStartTime
        : Math.min(Math.max(preservedTimestamp, manifestStartTime), latestPlayableTime);
    setPlaybackTimestamp(timestamp);
    setTimelineTimestamp(timestamp);
    setPlaybackSpeed(1);
    setPlaybackSpeedMenuAnchor(null);
  }, [manifest]);

  useEffect(() => {
    if (!hasPlayablePlaybackData) {
      setIsPlaying(false);
      setPlaybackSpeedMenuAnchor(null);
      return;
    }
    setPlaybackTimestamp((current) => Math.min(Math.max(current, playbackStart), playbackEnd));
    setTimelineTimestamp((current) => Math.min(Math.max(current, playbackStart), playbackEnd));
    setHoveredTimestamp(null);
    setIsPlaying(false);
  }, [hasPlayablePlaybackData, playbackEnd, playbackStart]);

  useEffect(() => {
    if (playbackError?.kind === "segment") setIsPlaying(false);
  }, [playbackError]);

  useEffect(() => {
    if (!hasPlayablePlaybackData || !manifest) return;
    const current = manifest.segments.find((segment) => {
      const start = Date.parse(segment.start_time);
      const end = Date.parse(segment.end_time);
      return playbackTimestamp >= start && playbackTimestamp < end;
    });
    if (!current) return;

    ensureSegmentWindow(current.index);
  }, [ensureSegmentWindow, hasPlayablePlaybackData, manifest, playbackTimestamp]);

  useEffect(() => {
    if (!isPlaying) return;

    const timeout = window.setTimeout(() => {
      setWasAutoPaused(true);
      setIsPlaying(false);
    }, PLAYBACK_AUTO_PAUSE_MS);

    return () => window.clearTimeout(timeout);
  }, [isPlaying]);

  useEffect(() => {
    if (!isPlaying || isBufferingPlaybackPosition) {
      playbackClockRef.current = null;
      return;
    }

    playbackClockRef.current = {
      wallTime: performance.now(),
      playbackTime: playbackTimestamp,
    };
    const interval = window.setInterval(() => {
      const clock = playbackClockRef.current;
      if (clock === null) return;
      const next = getPlaybackClockTimestamp(
        clock,
        performance.now(),
        playbackEnd,
        playbackStep,
        playbackSpeed
      );
      if (next <= playbackTimestamp) return;
      void commitPlaybackTimestamp(next).then((advanced) => {
        if (!advanced || next >= playbackEnd) setIsPlaying(false);
      });
    }, PLAYBACK_TICK_MS);

    return () => window.clearInterval(interval);
  }, [
    commitPlaybackTimestamp,
    isBufferingPlaybackPosition,
    isPlaying,
    playbackEnd,
    playbackSpeed,
    playbackStep,
    playbackTimestamp,
  ]);

  useEffect(
    () => () => {
      seekRequestIdRef.current += 1;
      if (hoverAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(hoverAnimationFrameRef.current);
      }
    },
    []
  );

  function handleApplyFilters() {
    const dateRangeChanged =
      draftFilters.startDate.getTime() !== filters.startDate.getTime() ||
      draftFilters.endDate.getTime() !== filters.endDate.getTime();

    setIsPlaying(false);
    setPlaybackSpeed(1);
    setPlaybackSpeedMenuAnchor(null);
    setFiltersApplied(true);
    setFilters(draftFilters);
    if (dateRangeChanged) {
      setQuality(
        getPlaybackQualityForDuration(
          draftFilters.endDate.getTime() - draftFilters.startDate.getTime()
        )
      );
    }
  }

  function handleReset() {
    const playbackRange = getTodayPlaybackRange();
    const nextFilters: AnalyticsFilters = {
      ...playbackRange,
      areaId: draftFilters.areaId,
    };
    setFiltersApplied(true);
    setFilters(nextFilters);
    setDraftFilters(nextFilters);
    setQuality(
      getPlaybackQualityForDuration(
        playbackRange.endDate.getTime() - playbackRange.startDate.getTime()
      )
    );
    setPlaybackTimestamp(playbackRange.startDate.getTime());
    setTimelineTimestamp(playbackRange.startDate.getTime());
    setIsPlaying(false);
    setPlaybackSpeed(1);
    setPlaybackSpeedMenuAnchor(null);
  }

  function resetPlaybackPosition() {
    setIsPlaying(false);
    void commitPlaybackTimestamp(playbackStart);
  }

  function startPlayback(): void {
    setWasAutoPaused(false);
    setIsPlaying(true);
  }

  function togglePlayback() {
    if (!hasPlayablePlaybackData) return;
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    setWasAutoPaused(false);
    const startTimestamp = playbackTimestamp >= playbackEnd ? playbackStart : timelineTimestamp;
    void commitPlaybackTimestamp(startTimestamp).then((buffered) => {
      if (buffered && startTimestamp < playbackEnd) startPlayback();
    });
  }

  function startSeekingPlaybackPosition() {
    if (!isSeekingPlaybackPositionRef.current) {
      isSeekingPlaybackPositionRef.current = true;
      resumePlaybackAfterSeekRef.current = isPlaying && !playbackSettings.pauseOnSeek;
      setIsPlaying(false);
    }

    setIsSeekingPlaybackPosition(true);
    if (hoverAnimationFrameRef.current != null) {
      window.cancelAnimationFrame(hoverAnimationFrameRef.current);
      hoverAnimationFrameRef.current = null;
    }
    setHoveredTimestamp(null);
  }

  function commitSeekingPlaybackPosition(timestamp: number): void {
    isSeekingPlaybackPositionRef.current = false;
    setIsSeekingPlaybackPosition(false);
    const resumePlayback = resumePlaybackAfterSeekRef.current;
    resumePlaybackAfterSeekRef.current = false;

    void commitPlaybackTimestamp(timestamp).then((buffered) => {
      if (buffered && resumePlayback && timestamp < playbackEnd) startPlayback();
    });
  }

  const hasUnappliedFilterChanges =
    draftFilters.areaId !== filters.areaId ||
    draftFilters.startDate.getTime() !== filters.startDate.getTime() ||
    draftFilters.endDate.getTime() !== filters.endDate.getTime();
  const playbackStatsNote = !filtersApplied
    ? "Select filters to load playback"
    : hasPlaybackRange
      ? `Current timeframe · At ${formatPlaybackTime(playbackTimestamp, includesMultipleDays)}`
      : "Waiting for playback frame";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2.5,
        minHeight: "100%",
        pb: `${SCRUBBER_HEIGHT + 16}px`,
      }}
    >
      <AnalyticsFilterDashboard
        filters={draftFilters}
        areas={areas}
        onFiltersChange={(nextFilters) => {
          setDraftFilters(nextFilters);
          if (nextFilters.areaId !== draftFilters.areaId) {
            browserStorage.write(storageEntries.preferredArea(deploymentId), nextFilters.areaId);
          }
        }}
        onApply={handleApplyFilters}
        applyDisabled={draftFilters.areaId.length === 0 || !hasUnappliedFilterChanges}
        onReset={handleReset}
        onExport={() => undefined}
        isExporting={false}
        showExport={false}
        showAllAreas={false}
        showQuickSelect={false}
        initialExpanded
        dateRangeLimits={PLAYBACK_DATE_RANGE_CONFIG}
        title="Playback Filters"
        description="Choose an area and a 10-minute to 24-hour range to review historical activity."
        additionalFilters={
          <LayersSelect
            showLabel
            showHeatmap={showHeatmap}
            onShowHeatmapChange={setShowHeatmap}
            showSensors={showSensors}
            onShowSensorsChange={setShowSensors}
            showDetections={showDetections}
            onShowDetectionsChange={setShowDetections}
            showTracks={showTracks}
            onShowTracksChange={setShowTracks}
          />
        }
      />

      {playbackError != null ? (
        <Callout
          severity="error"
          title={
            playbackError.kind === "manifest"
              ? "Playback data unavailable"
              : "Playback segment unavailable"
          }
        >
          {playbackError.message}
          {playbackError.oversized ? " Choose a lower playback quality and retry." : null}
          {playbackError.kind === "segment" && playbackError.segmentIndex != null ? (
            <Button
              size="small"
              sx={{ mt: 1, display: "block" }}
              onClick={() => {
                if (playbackError.segmentIndex !== undefined)
                  void retrySegment(playbackError.segmentIndex);
              }}
            >
              Retry segment
            </Button>
          ) : null}
        </Callout>
      ) : referenceFrameMismatch && playbackFrame != null && transform != null ? (
        <Callout severity="warning" title="Playback coordinates unavailable">
          This frame uses reference frame {playbackFrame.reference_frame_id}, but the selected map
          is calibrated for {transform.sourceFrame}.
        </Callout>
      ) : hasNoPlaybackData ? (
        <Callout severity="info" title="No playback data in this range">
          {hasPlaybackRange
            ? "No playback frames are available for the selected area and time range."
            : `Choose a time range between ${formatPlaybackTime(manifestStart ?? 0, true)} and ${formatPlaybackTime(manifestEnd ?? 0, true)}.`}
        </Callout>
      ) : null}

      <Box
        aria-label="Playback statistics"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <MetricCard
          label="Population"
          value={
            playbackPopulationPoint?.averageTotalPopulation == null
              ? "—"
              : formatCount(playbackPopulationPoint.averageTotalPopulation)
          }
          valueLabel="Average"
          valueSuffix="people"
          secondaryValue={{
            label: "Peak",
            value:
              playbackPopulationPoint?.peakTotalPopulation == null
                ? "—"
                : formatCount(playbackPopulationPoint.peakTotalPopulation),
            valueSuffix: "people",
          }}
          issue={populationMetricIssue}
          contentState={populationMetricContentState}
          note={playbackStatsNote}
          icon={<PeopleIcon sx={{ fontSize: "0.875rem" }} />}
        />
        <MetricCard
          label="Average Density"
          value={playbackDensity == null ? "—" : playbackDensity.toFixed(4)}
          valueSuffix="people/m²"
          issue={densityMetricIssue}
          contentState={densityMetricContentState}
          note={playbackStatsNote}
          icon={<TrendingUpIcon sx={{ fontSize: "0.875rem" }} />}
        />
      </Box>

      <ConsoleSurface sx={{ p: 1.5 }}>
        <Typography variant="overline" sx={{ display: "block", px: 1, pb: 1, fontWeight: 700 }}>
          Area View
        </Typography>
        <Box sx={{ position: "relative", height: MAP_HEIGHT, minHeight: 0 }}>
          {!filtersApplied || hasNoPlaybackData ? (
            <Box
              sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {!filtersApplied ? "Select playback filters" : "No Data Available"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {!filtersApplied
                    ? "Choose an area and date range above, then select Apply to load playback data."
                    : "No playback frames are available for the selected area and time range."}
                </Typography>
              </Box>
            </Box>
          ) : (
            <FacilitySchematic
              deploymentId={deploymentId}
              areaId={selectedArea?.id}
              liveTracks={showDetections ? liveTracks : []}
              trails={showTracks ? trails : []}
              heatmapTracks={liveTracks}
              heatmapTransform={transform}
              heatmapMapSize={floorplanImageSize}
              showHeatmap={showHeatmap}
              showSensors={showSensors}
              observationAreas={observationAreas}
              aoiBoundaries={aoiMapLayers.boundaries}
            />
          )}
          {isPlaybackMapBuffering ? (
            <Box
              role="status"
              aria-label="Buffering playback frames"
              sx={(theme) => ({
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.background.default, 0.38),
                pointerEvents: "none",
              })}
            >
              <CircularProgress color="secondary" aria-label="Buffering playback frames" />
            </Box>
          ) : null}
        </Box>
      </ConsoleSurface>

      <Box sx={{ height: { xs: 340, md: 400 }, minHeight: { xs: 340, md: 400 } }}>
        <PopulationTrendAnalyticsChart
          data={populationTrend}
          loading={populationAnalytics.loading}
          waitingForFilters={!filtersApplied}
          error={populationAnalytics.error}
          xAxisRange={chartXAxisRange}
          playbackTimestamp={timelineTimestamp}
          additionalPins={timelineHoverPins}
        />
      </Box>

      <QueueWaitTrendChart
        xAxisRange={chartXAxisRange}
        queues={queueMetrics.data}
        loading={queueMetrics.isLoading}
        waitingForFilters={!filtersApplied}
        error={queueMetrics.error}
        playbackTimestamp={timelineTimestamp}
        additionalPins={timelineHoverPins}
      />

      <ConsoleSurface
        component="section"
        aria-label="Playback timeline"
        sx={(theme) => ({
          position: "sticky",
          bottom: 0,
          // Keep the sticky timeline above page content while allowing its
          // portaled MUI tooltips to render above the timeline surface.
          zIndex: theme.zIndex.tooltip - 1,
          p: 1.5,
          pb: 4,
          backgroundColor: theme.palette.background.paper,
          borderColor: alpha(theme.palette.secondary.main, 0.28),
          boxShadow: 4,
        })}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
        >
          <Box>
            <Typography variant="overline" sx={{ display: "block" }}>
              Playback timeline
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              {isBufferingPlaybackPosition ? <CircularProgress size={14} /> : null}
              <Typography variant="caption" color="text.secondary">
                {!filtersApplied
                  ? "Select filters to begin"
                  : hasNoPlaybackData
                    ? "No playback data available"
                    : isBufferingPlaybackPosition
                      ? "Buffering playback frames…"
                      : loadingSegmentIndexes.size > 0
                        ? "Loading requested playback frames…"
                        : `One frame per ${playbackStep / 1_000} seconds · ${isPlaying ? "Playing at" : "Speed"} ${playbackSpeed}×`}
              </Typography>
            </Box>
          </Box>
          <Typography variant="subtitle2" sx={{ fontVariantNumeric: "tabular-nums" }}>
            {formatPlaybackTime(timelineTimestamp, includesMultipleDays)}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 0.5 }}>
          <Box sx={{ display: "flex", flexShrink: 0, gap: 0.25 }}>
            <Tooltip title="Jump to first frame">
              <IconButton
                aria-label="Jump to first frame"
                disabled={!hasPlayablePlaybackData}
                onClick={resetPlaybackPosition}
              >
                <FirstPageIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Tooltip title={isPlaying ? "Pause playback" : "Play playback"}>
                <IconButton
                  aria-label={isPlaying ? "Pause playback" : "Play playback"}
                  color="secondary"
                  disabled={!hasPlayablePlaybackData}
                  onClick={togglePlayback}
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
              </Tooltip>
              {wasAutoPaused ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.625rem", lineHeight: 1.2, whiteSpace: "nowrap" }}
                >
                  Auto-paused after {PLAYBACK_AUTO_PAUSE_MINUTES} min
                </Typography>
              ) : null}
            </Box>
            <Button
              aria-controls={playbackSpeedMenuAnchor === null ? undefined : "playback-speed-menu"}
              aria-expanded={playbackSpeedMenuAnchor === null ? undefined : true}
              aria-haspopup="menu"
              aria-label="Playback speed"
              disabled={!hasPlayablePlaybackData}
              size="small"
              variant="text"
              onClick={(event) => setPlaybackSpeedMenuAnchor(event.currentTarget)}
              sx={{ minWidth: 48, fontVariantNumeric: "tabular-nums" }}
            >
              {playbackSpeed}×
            </Button>
            <Menu
              id="playback-speed-menu"
              anchorEl={playbackSpeedMenuAnchor}
              open={playbackSpeedMenuAnchor !== null}
              onClose={() => setPlaybackSpeedMenuAnchor(null)}
              sx={(theme) => ({ zIndex: theme.zIndex.tooltip + 1 })}
            >
              {PLAYBACK_SPEEDS.map((speed) => (
                <MenuItem
                  key={speed}
                  selected={speed === playbackSpeed}
                  onClick={() => {
                    setPlaybackSpeed(speed);
                    setPlaybackSpeedMenuAnchor(null);
                  }}
                  sx={{ minWidth: 112, justifyContent: "space-between", gap: 2 }}
                >
                  {speed}×{speed === playbackSpeed ? <CheckIcon fontSize="small" /> : null}
                </MenuItem>
              ))}
            </Menu>
            <Tooltip title="Playback settings">
              <IconButton
                aria-label="Playback settings"
                onClick={() => setIsPlaybackSettingsOpen(true)}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Box
            sx={{ position: "relative", flex: 1 }}
            onMouseMove={(event) => {
              const isHoveringPlaybackThumb =
                event.target instanceof Element &&
                event.target.closest(".MuiSlider-thumb, .MuiSlider-valueLabel") != null;
              if (isSeekingPlaybackPosition || isHoveringPlaybackThumb) {
                if (hoverAnimationFrameRef.current != null) {
                  window.cancelAnimationFrame(hoverAnimationFrameRef.current);
                  hoverAnimationFrameRef.current = null;
                }
                setHoveredTimestamp(null);
                return;
              }

              const rail = event.currentTarget.getBoundingClientRect();
              if (rail.width <= 0) return;
              const position = Math.min(Math.max(event.clientX - rail.left, 0), rail.width);
              const frameIndex = Math.round((position / rail.width) * frameCount);
              const timestamp = Math.min(playbackEnd, playbackStart + frameIndex * playbackStep);
              if (hoverAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(hoverAnimationFrameRef.current);
              }
              hoverAnimationFrameRef.current = window.requestAnimationFrame(() => {
                setHoveredTimestamp(timestamp);
                hoverAnimationFrameRef.current = null;
              });
            }}
            onMouseLeave={() => {
              if (hoverAnimationFrameRef.current != null) {
                window.cancelAnimationFrame(hoverAnimationFrameRef.current);
                hoverAnimationFrameRef.current = null;
              }
              setHoveredTimestamp(null);
            }}
          >
            <Slider
              aria-label="Playback position"
              aria-valuetext={formatPlaybackTime(timelineTimestamp, includesMultipleDays)}
              value={hasPlayablePlaybackData ? timelineTimestamp : playbackStart}
              min={playbackStart}
              max={Math.max(playbackStart, playbackEnd)}
              step={playbackStep}
              shiftStep={playbackStep}
              disabled={!hasPlayablePlaybackData}
              onMouseDown={startSeekingPlaybackPosition}
              onTouchStart={startSeekingPlaybackPosition}
              onChange={(_, value) => {
                if (typeof value !== "number") return;
                startSeekingPlaybackPosition();
                setTimelineTimestamp(value);
              }}
              onChangeCommitted={(_, value) => {
                if (typeof value === "number") commitSeekingPlaybackPosition(value);
              }}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => formatPlaybackTime(value, includesMultipleDays)}
              sx={(theme) => ({
                py: 1.25,
                "& .MuiSlider-thumb": { transition: "none" },
                "& .MuiSlider-rail": {
                  height: 10,
                  opacity: 1,
                  backgroundColor: theme.palette.background.paper,
                  backgroundImage: `${timelineAvailabilityBackground}, repeating-linear-gradient(90deg, transparent 0, transparent calc(${majorTickSpacing}% - 1px), ${alpha(theme.palette.text.primary, 0.35)} calc(${majorTickSpacing}% - 1px), ${alpha(theme.palette.text.primary, 0.35)} ${majorTickSpacing}%)`,
                },
                "& .MuiSlider-track": {
                  height: 10,
                  border: "none",
                  backgroundColor: "transparent",
                },
                "& .MuiSlider-valueLabel": {
                  top: 32,
                  borderRadius: 1,
                  backgroundColor: "secondary.main",
                  color: "background.default",
                  fontVariantNumeric: "tabular-nums",
                  transform: "translateY(0) scale(0)",
                  transformOrigin: "top center",
                  "&::before": {
                    top: 0,
                    bottom: "auto",
                    transform: "translate(-50%, -50%) rotate(45deg)",
                  },
                },
                "& .MuiSlider-valueLabelOpen": {
                  transform: "translateY(0) scale(1)",
                },
              })}
            />
            {hoveredTimestamp != null ? (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: `${((hoveredTimestamp - playbackStart) / playbackDuration) * 100}%`,
                  top: 14,
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pointerEvents: "none",
                  transform: "translateX(-50%)",
                }}
              >
                <Box sx={{ width: 2, height: 14, backgroundColor: "warning.light" }} />
                <Typography
                  variant="caption"
                  sx={{
                    mt: 1.25,
                    borderRadius: 1,
                    bgcolor: "warning.light",
                    color: "background.default",
                    px: 0.75,
                    py: 0.25,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatPlaybackTime(hoveredTimestamp, includesMultipleDays)}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </ConsoleSurface>

      <Dialog
        open={isPlaybackSettingsOpen}
        onClose={() => setIsPlaybackSettingsOpen(false)}
        aria-labelledby="playback-settings-title"
      >
        <DialogTitle id="playback-settings-title">Playback settings</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                id="playback-quality-label"
                variant="body2"
                sx={{ flexShrink: 0, fontWeight: 600 }}
              >
                Playback Quality
              </Typography>
              <Select<string>
                aria-labelledby="playback-quality-label"
                value={quality}
                sx={{ flex: 1 }}
                onChange={(event) => {
                  const nextQuality = event.target.value;
                  if (!isPlaybackQuality(nextQuality) || nextQuality === quality) return;
                  setIsPlaying(false);
                  qualityChangeTimestampRef.current = timelineTimestamp;
                  setQuality(nextQuality);
                }}
              >
                {PLAYBACK_DATE_RANGE_CONFIG.qualityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <FormHelperText>
              Quality is selected from the date range. Your selection is kept until the range
              changes.
            </FormHelperText>
            <FormHelperText>Lower quality uses less temporal detail.</FormHelperText>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={playbackSettings.pauseOnSeek}
                onChange={(event) => {
                  const nextSettings = { pauseOnSeek: event.target.checked };
                  setPlaybackSettings(nextSettings);
                  browserStorage.write(storageEntries.playbackSettings, nextSettings);
                }}
              />
            }
            label="Pause when seeking"
          />
          <Typography variant="body2" color="text.secondary">
            When disabled, playback continues after moving to another point on the timeline.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPlaybackSettingsOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
